import { useState } from "react";
import {
    Rocket,
    TrendingUp,
    Users,
    Globe,
    MapPin,
    Briefcase,
    Clock3,
    ArrowRight,
    UploadCloud,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHY_JOIN = [
    { icon: Rocket, title: "Impactful Work", desc: "Work on a live fintech product that people use to exchange real value every day." },
    { icon: TrendingUp, title: "Growth & Learning", desc: "Learn across crypto, payments and financial technology as the platform grows." },
    { icon: Users, title: "Collaborative Culture", desc: "Small, focused teams where your ideas and ownership genuinely matter." },
    { icon: Globe, title: "Remote / Hybrid Flexibility", desc: "Remote and hybrid opportunities depending on the role and team." },
];

const JOBS = [
    {
        title: "Customer Support Executive",
        department: "Support",
        type: "Full-time",
        location: "Remote",
        desc: "Help users with their USDT to INR exchange queries and guide them through the exchange process with clarity and care.",
    },
    {
        title: "Backend Engineer (Python / FastAPI)",
        department: "Engineering",
        type: "Full-time",
        location: "Remote / Hybrid",
        desc: "Build and maintain the services behind live rates, exchange processing and transaction handling on the DtOrbit platform.",
    },
    {
        title: "Operations Associate",
        department: "Operations",
        type: "Full-time",
        location: "Hybrid",
        desc: "Support day-to-day exchange operations, verification workflows and process improvements across the platform.",
    },
];

const inputClass =
    "w-full rounded-xl border border-[#D5E4FF] bg-white px-4 py-3 text-sm text-[#0F1B3D] placeholder:text-[#8494B0] outline-none transition-colors focus:border-[#1E56FF] focus:ring-2 focus:ring-[#1E56FF]/15";

export default function Careers() {
    usePageMeta({
        title: "Careers at DtOrbit | Build the Future of Crypto Exchange",
        description:
            "Join DtOrbit and help build a simple, secure USDT to INR exchange platform. Explore current opportunities and apply online.",
        path: "/careers",
    });

    const [form, setForm] = useState({
        full_name: "",
        email: "",
        phone: "",
        position: "",
        message: "",
    });
    const [resume, setResume] = useState(null);
    const [status, setStatus] = useState({ state: "idle", message: "" });

    const applyFor = (title) => {
        setForm((prev) => ({ ...prev, position: title }));
        document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
    };

    const onChange = (e) =>
        setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!resume) {
            setStatus({ state: "error", message: "Please attach your resume (PDF, DOC or DOCX, max 5MB)." });
            return;
        }
        setStatus({ state: "loading", message: "" });
        try {
            const data = new FormData();
            Object.entries(form).forEach(([k, v]) => data.append(k, v));
            data.append("resume", resume);
            const res = await fetch(`${API}/applications`, { method: "POST", body: data });
            const json = await res.json();
            if (!res.ok) throw new Error(json.detail || "Submission failed");
            setStatus({ state: "success", message: json.message || "Application submitted successfully. We will review it and get back to you." });
            setForm({ full_name: "", email: "", phone: "", position: "", message: "" });
            setResume(null);
            e.target.reset();
        } catch (err) {
            setStatus({ state: "error", message: err.message || "Something went wrong. Please try again." });
        }
    };

    return (
        <>
            <PageHero
                testid="careers-hero"
                label="Careers"
                title="Build the Future with"
                highlight="DtOrbit"
                description="Join a team building simple, secure crypto-to-fiat exchange experiences. Help shape the future of USDT to INR exchange and digital finance."
            />

            <section data-testid="careers-why-join" className="relative py-14 md:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Why Join DtOrbit
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                        {WHY_JOIN.map(({ icon: Icon, title, desc }, i) => (
                            <Reveal key={title} delay={i * 90}>
                                <div className="glass card-hover rounded-2xl p-6 h-full" data-testid={`careers-why-${i + 1}`}>
                                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#EAF2FF] border border-[#D5E4FF]">
                                        <Icon className="w-5 h-5 text-[#1E56FF]" />
                                    </span>
                                    <h3 className="font-display mt-4 text-base font-bold text-[#0F1B3D]">
                                        {title}
                                    </h3>
                                    <p className="mt-2 text-sm text-[#5B6B8C] leading-relaxed">
                                        {desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <div className="mt-8 grid md:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="glass rounded-2xl p-7 h-full" data-testid="careers-culture">
                                <h2 className="font-display text-xl font-bold text-[#0F1B3D]">
                                    Our Work Culture
                                </h2>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    We keep things focused and practical: clear
                                    goals, honest communication and ownership
                                    over your work. Teams are small by design,
                                    so every contribution is visible and every
                                    voice is heard.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={120}>
                            <div className="glass rounded-2xl p-7 h-full" data-testid="careers-growth">
                                <h2 className="font-display text-xl font-bold text-[#0F1B3D]">
                                    Growth &amp; Learning
                                </h2>
                                <p className="mt-2.5 text-sm text-[#5B6B8C] leading-relaxed">
                                    Working at the intersection of crypto and
                                    finance means constant learning. You will
                                    work across exchange technology, payments
                                    and operations — with room to grow as
                                    DtOrbit grows.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section data-testid="careers-openings" className="relative py-14 md:py-20 border-t border-[#E4EAF6] bg-white/60">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Current Opportunities
                        </h2>
                    </Reveal>
                    <div className="mt-10 grid md:grid-cols-3 gap-5 md:gap-6">
                        {JOBS.map((job, i) => (
                            <Reveal key={job.title} delay={i * 100}>
                                <div className="glass card-hover rounded-2xl p-6 h-full flex flex-col" data-testid={`job-card-${i + 1}`}>
                                    <h3 className="font-display text-lg font-bold text-[#0F1B3D]">
                                        {job.title}
                                    </h3>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-3 py-1 text-[11px] font-semibold text-[#1E56FF]">
                                            <Briefcase className="w-3 h-3" />
                                            {job.department}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-3 py-1 text-[11px] font-semibold text-[#1E56FF]">
                                            <Clock3 className="w-3 h-3" />
                                            {job.type}
                                        </span>
                                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF2FF] border border-[#D5E4FF] px-3 py-1 text-[11px] font-semibold text-[#1E56FF]">
                                            <MapPin className="w-3 h-3" />
                                            {job.location}
                                        </span>
                                    </div>
                                    <p className="mt-4 text-sm text-[#5B6B8C] leading-relaxed flex-1">
                                        {job.desc}
                                    </p>
                                    <button
                                        type="button"
                                        data-testid={`job-apply-${i + 1}`}
                                        onClick={() => applyFor(job.title)}
                                        className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1E56FF] hover:text-[#0A1F9E] transition-colors group"
                                    >
                                        Apply Now
                                        <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </button>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section id="apply" data-testid="careers-apply" className="relative py-14 md:py-20 border-t border-[#E4EAF6] scroll-mt-24">
                <div className="max-w-3xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1B3D]">
                            Apply Now
                        </h2>
                        <p className="mt-3 text-sm md:text-base text-[#5B6B8C]">
                            Fill in the form below and attach your resume. We
                            review every application.
                        </p>
                    </Reveal>
                    <Reveal delay={100}>
                        <form
                            onSubmit={onSubmit}
                            data-testid="careers-application-form"
                            className="glass rounded-2xl p-6 md:p-8 mt-8 flex flex-col gap-5"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label htmlFor="full_name" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Full Name *</label>
                                    <input id="full_name" name="full_name" required value={form.full_name} onChange={onChange} data-testid="apply-name-input" className={inputClass} placeholder="Your full name" />
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Email *</label>
                                    <input id="email" name="email" type="email" required value={form.email} onChange={onChange} data-testid="apply-email-input" className={inputClass} placeholder="you@example.com" />
                                </div>
                                <div>
                                    <label htmlFor="phone" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Phone</label>
                                    <input id="phone" name="phone" value={form.phone} onChange={onChange} data-testid="apply-phone-input" className={inputClass} placeholder="+91 …" />
                                </div>
                                <div>
                                    <label htmlFor="position" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Position *</label>
                                    <select id="position" name="position" required value={form.position} onChange={onChange} data-testid="apply-position-select" className={inputClass}>
                                        <option value="">Select a position</option>
                                        {JOBS.map((job) => (
                                            <option key={job.title} value={job.title}>{job.title}</option>
                                        ))}
                                        <option value="Other">Other / General Application</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="resume" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Resume Upload *</label>
                                <label
                                    htmlFor="resume"
                                    data-testid="apply-resume-upload"
                                    className="flex items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#D5E4FF] bg-white/70 px-4 py-6 text-sm text-[#5B6B8C] cursor-pointer transition-colors hover:border-[#1E56FF]/50 hover:bg-[#F0F6FF]"
                                >
                                    <UploadCloud className="w-5 h-5 text-[#1E56FF]" />
                                    {resume ? resume.name : "Choose PDF, DOC or DOCX (max 5MB)"}
                                </label>
                                <input
                                    id="resume"
                                    name="resume"
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    className="sr-only"
                                    onChange={(e) => setResume(e.target.files?.[0] || null)}
                                />
                            </div>
                            <div>
                                <label htmlFor="message" className="block text-xs font-bold text-[#3D4C6E] mb-1.5">Message</label>
                                <textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} data-testid="apply-message-input" className={inputClass} placeholder="Tell us a little about yourself…" />
                            </div>
                            {status.message && (
                                <p
                                    data-testid="apply-form-status"
                                    className={`text-sm font-semibold ${
                                        status.state === "success" ? "text-emerald-600" : "text-rose-500"
                                    }`}
                                >
                                    {status.message}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="apply-submit-button"
                                disabled={status.state === "loading"}
                                className="btn-primary self-start disabled:opacity-60 disabled:pointer-events-none"
                            >
                                {status.state === "loading" ? "Submitting…" : "Submit Application"}
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </form>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
